import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminApiError } from '../../api/admin-client.js';
import {
  fetchAdminMedia,
  fetchAdminMediaAsset,
  fetchProductImages,
  linkProductImage,
  reorderProductImages,
  setPrimaryProductImage,
  unlinkProductImage,
  updateProductImage,
  uploadAdminMedia,
} from '../api/media-api.js';
import type { MediaListParams } from '../types/media.js';

async function requireToken(getToken: () => Promise<string | null>): Promise<string> {
  const token = await getToken();
  if (!token) {
    throw new AdminApiError('AUTHENTICATION_ERROR', 'Your session has expired. Please sign in again.', 401);
  }
  return token;
}

export function useAdminMediaLibrary(params: MediaListParams) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: ['admin', 'media', 'library', params],
    queryFn: async ({ signal }) => fetchAdminMedia(params, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true,
    staleTime: 15_000,
  });
}

/** Single-asset preview lookup, e.g. a product's OG image (stored as a bare media_asset_id, not a product_images link row). Disabled when `id` is null/empty -- callers pass the current form value directly. */
export function useAdminMediaAsset(id: string | null | undefined) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: ['admin', 'media', 'asset', id ?? ''],
    queryFn: async ({ signal }) =>
      fetchAdminMediaAsset(id!, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true && Boolean(id),
    staleTime: 60_000,
  });
}

export function useUploadMedia() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => uploadAdminMedia(file, { token: await requireToken(getToken) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'media', 'library'] });
    },
  });
}

function productImagesKey(productId: string) {
  return ['admin', 'media', 'product-images', productId] as const;
}

export function useProductImages(productId: string | undefined) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: productImagesKey(productId ?? ''),
    queryFn: async ({ signal }) =>
      fetchProductImages(productId!, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true && Boolean(productId),
    staleTime: 15_000,
  });
}

export function useProductImageMutations(productId: string) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: productImagesKey(productId) });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'media', 'library'] });
  };

  const link = useMutation({
    mutationFn: async (input: { mediaAssetId: string; altText?: string | null; asPrimary?: boolean }) =>
      linkProductImage(productId, input, { token: await requireToken(getToken) }),
    onSuccess: invalidate,
  });

  const reorder = useMutation({
    mutationFn: async (orderedImageIds: string[]) =>
      reorderProductImages(productId, orderedImageIds, { token: await requireToken(getToken) }),
    onSuccess: invalidate,
  });

  const setPrimary = useMutation({
    mutationFn: async (imageId: string) =>
      setPrimaryProductImage(productId, imageId, { token: await requireToken(getToken) }),
    onSuccess: invalidate,
  });

  const updateAlt = useMutation({
    mutationFn: async ({ imageId, altText }: { imageId: string; altText: string | null }) =>
      updateProductImage(imageId, { altText }, { token: await requireToken(getToken) }),
    onSuccess: invalidate,
  });

  const unlink = useMutation({
    mutationFn: async (imageId: string) =>
      unlinkProductImage(imageId, { token: await requireToken(getToken) }),
    onSuccess: invalidate,
  });

  return { link, reorder, setPrimary, updateAlt, unlink };
}
